import i18next from 'i18next'
import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/domains/shared/components/ui/Button'
import { isStaleChunkError } from '@/domains/shared/utils/staleChunk'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  isStaleChunk: boolean
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, isStaleChunk: false }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, isStaleChunk: isStaleChunkError(error) }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // biome-ignore lint/suspicious/noConsole: logging boundary errors is important for debugging
    console.error('ErrorBoundary caught:', error, info.componentStack)
  }

  private handleRetry = (): void => {
    if (this.state.isStaleChunk) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, isStaleChunk: false })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      const keys = this.state.isStaleChunk
        ? {
            title: 'errors.boundary.staleTitle',
            description: 'errors.boundary.staleDescription',
            retry: 'errors.boundary.staleRetry',
          }
        : {
            title: 'errors.boundary.title',
            description: 'errors.boundary.description',
            retry: 'errors.boundary.retry',
          }
      return (
        <div className="flex flex-1 flex-col items-center justify-center gap-4 p-8">
          <h2 className="text-2xl font-semibold tracking-tight text-foreground">
            {i18next.t(keys.title)}
          </h2>
          <p className="text-sm text-muted-foreground max-w-md text-center">
            {i18next.t(keys.description)}
          </p>
          <Button variant="outline" size="sm" onClick={this.handleRetry}>
            {i18next.t(keys.retry)}
          </Button>
        </div>
      )
    }

    return this.props.children
  }
}
